import { useId, useMemo } from "react";
import PropTypes from "prop-types";
import { formatNumber, muscleLabel, setLine, templateLabel } from "./lib/format";
import { BEST_WEEKS, bestVerdict, usePastBests } from "./lib/pastBests";
import { e1rmBests, muscleSets } from "./lib/sessionSets";

const plural = (n, one, many) => `${formatNumber(n)} ${Number(n) === 1 ? one : many}`;

/** Minutes from start to finish, or null when either is missing. */
function minutesOf(session) {
  const start = Date.parse(session.started_at ?? "");
  const end = Date.parse(session.finished_at ?? "");
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return Math.round((end - start) / 60000);
}

/** The line under the estimates: what they are, and what a best is measured against. */
function bestsNote(status, past, units) {
  const lead = past ? "That day's estimate from each main lift's best set" : "Today's estimate from each main lift's best set";
  if (status === "ready") {
    return past
      ? `${lead}. A new best beat every earlier session of it in the last ${BEST_WEEKS} weeks.`
      : `${lead}. A new best beats every session of it in the last ${BEST_WEEKS} weeks.`;
  }
  if (status === "loading") return `${lead} — comparing it with your past sessions…`;
  if (status === "units") return `${lead} — not compared: your past sessions are summed up in ${units}.`;
  return `${lead} — couldn't compare it with your past sessions.`;
}

/**
 * A session's summary (walkthrough 7): how much was done, the sets each
 * muscle got (fractional, D6), each main lift's estimated max today with
 * the set it came from — and, against the sessions before it (GET
 * summary/, 26 weeks), whether it's a new best (W16) — and what happened
 * to the feedback.
 *
 * `past` (History's session page): the same totals for a session read
 * later — "Totals", the max "that day" against the sessions before it,
 * and no feedback line (that page shows the feedback itself).
 */
function SessionSummary({ session, exercises, past = false }) {
  const id = useId();
  const byKey = useMemo(() => new Map((exercises ?? []).map((e) => [e.key, e])), [exercises]);
  const sets = session.sets ?? [];
  const units = session.units ?? "lb";
  const perMuscle = muscleSets(sets, byKey);
  const bests = e1rmBests(sets, byKey);
  // nothing to compare (no main-lift estimate): summary/ isn't asked
  const before = usePastBests(session, { past, enabled: bests.length > 0 });
  const exerciseCount = new Set(sets.map((set) => set.exercise)).size;
  const minutes = minutesOf(session);
  const rated = Object.keys(session.feedback ?? {});

  return (
    <section aria-labelledby={`${id}-title`} className="flex flex-col gap-4 rounded-lg border bg-card p-4 md:p-6">
      <div>
        <h2 id={`${id}-title`} className="m-0 font-display text-xl font-semibold">
          {past ? "Totals" : "Session done"}
        </h2>
        <p className="mt-1 mb-0 tabular-nums">
          {templateLabel(session.template)}: {plural(sets.length, "set", "sets")} of{" "}
          {plural(exerciseCount, "exercise", "exercises")}
          {minutes !== null ? ` in ${plural(minutes, "minute", "minutes")}` : ""}.
        </p>
      </div>

      {perMuscle.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 id={`${id}-muscles`} className="m-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Sets per muscle
          </h3>
          <ul aria-labelledby={`${id}-muscles`} className="m-0 grid list-none gap-x-6 gap-y-1 p-0 sm:grid-cols-2">
            {perMuscle.map((row) => (
              <li key={row.muscle} className="flex justify-between gap-3 text-sm">
                <span>{row.label}</span>{" "}
                <span className="tabular-nums">{plural(row.sets, "set", "sets")}</span>
              </li>
            ))}
          </ul>
          <p className="m-0 text-xs text-muted-foreground">
            A set counts whole for the muscle an exercise works directly, and half for one it helps.
          </p>
        </div>
      )}

      {bests.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 id={`${id}-e1rm`} className="m-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {past ? "Estimated max that day" : "Estimated max today"}
          </h3>
          <ul aria-labelledby={`${id}-e1rm`} className="m-0 flex list-none flex-col gap-1 p-0">
            {bests.map((best) => {
              const exercise = byKey.get(best.exercise);
              // A bodyweight lift's max is an effective load (bodyweight +
              // added − band, D17): "~196 lb" alone read as a 196 lb chin-up.
              const withBody = exercise?.load_kind === "bodyweight";
              const earlier = before.bests.get(best.exercise);
              const verdict = bestVerdict(best, earlier);
              // Whole numbers, unless a new best rounds to the old one's:
              // "~193 lb … (was ~193)" reads as no change — both to one
              // decimal then, as they're compared.
              const tenths =
                verdict === "best" && Math.round(best.e1rm) === Math.round(earlier.e1rm);
              const shown = (value) => formatNumber(tenths ? Math.round(value * 10) / 10 : Math.round(value));
              return (
                <li key={best.exercise} className="text-sm tabular-nums">
                  <span className="font-medium">
                    {best.name}: ~{shown(best.e1rm)} {units}
                    {withBody ? " including bodyweight," : ""}
                  </span>{" "}
                  <span className="text-muted-foreground">from {setLine(best.set, units, exercise)}</span>
                  {verdict === "best" && (
                    <span className="font-medium"> — a new best (was ~{shown(earlier.e1rm)})</span>
                  )}
                  {verdict === "first" && (
                    <span className="text-muted-foreground"> — the first in {BEST_WEEKS} weeks</span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="m-0 text-xs text-muted-foreground">{bestsNote(before.status, past, before.units)}</p>
        </div>
      )}

      {!past && (
        <p className="m-0 text-sm text-muted-foreground">
          {rated.length > 0
            ? `Feedback saved for ${rated.includes("all") ? "every muscle" : rated.map((m) => muscleLabel(m).toLowerCase()).join(", ")} — the next plan reads it.`
            : "No feedback, so each muscle keeps its weekly sets as they are."}
        </p>
      )}
    </section>
  );
}

SessionSummary.propTypes = {
  /** The finished TrainingSession (queued sets may be passed in too). */
  session: PropTypes.shape({
    template: PropTypes.string,
    units: PropTypes.string,
    started_at: PropTypes.string,
    finished_at: PropTypes.string,
    sets: PropTypes.arrayOf(PropTypes.object),
    feedback: PropTypes.object,
  }).isRequired,
  exercises: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Read later (History): "Totals", the max "that day", no feedback line. */
  past: PropTypes.bool,
};

export default SessionSummary;
